import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** ボタンのバリアント */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** ボタンのサイズ */
  size?: 'sm' | 'md' | 'lg';
  /** ローディング状態 */
  loading?: boolean;
  /** アイコン（左側） */
  leftIcon?: ReactNode;
  /** 全幅表示 */
  fullWidth?: boolean;
}

/** @graph-connects none */
const variantStyles = {
  primary:
    'bg-gradient-to-r from-[var(--color-accent-primary)] to-emerald-400 hover:from-[var(--color-accent-hover)] hover:to-emerald-500 active:from-[var(--color-accent-active)] active:to-emerald-600 text-white shadow-[var(--shadow-glow)]',
  secondary:
    'bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-hover)] active:bg-[var(--color-bg-active)] text-[var(--color-text-primary)] border border-[var(--color-border)] hover:border-[var(--color-border-hover)]',
  ghost:
    'bg-transparent hover:bg-[var(--color-bg-hover)] active:bg-[var(--color-bg-active)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
  danger: 'bg-[var(--color-error)] hover:opacity-90 active:opacity-80 text-white',
};

/** @graph-connects none */
const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2.5',
};

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business 汎用ボタン。primary（エメラルドグラデーション）/secondary/ghost/dangerバリアントとサイズ、ローディング対応
 * @graph-connects none
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = (disabled ?? false) || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`
          inline-flex items-center justify-center
          font-medium rounded-lg cursor-pointer
          transition-all duration-[var(--transition-fast)]
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${fullWidth ? 'w-full' : ''}
          ${className}
        `}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon
        )}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
